
/* ==========================================================================
   SEO, GEO SCHEMAS & ROUTING ENGINE (/database/seo.js)
   ========================================================================== */

import { state, clearAllSelectedComponents, setCaseModeSelection, resolveCaseModeId, setSelectedComponent } from './state.js?v=20260819-audit2';
import { getCaseBySlug, getCaseIndexBySlug, getCaseByName } from './js/runtime-index.js?v=20260819-audit2';
import { formatCaseBrandTitle, slugifyUrlSegment, caseRouteSlug, escapeHtml, generateBuildShareTitle, AIO_COMBOBOX_IDS, syncComboboxInputValues, parseSpecNum } from './utils.js?v=20260819-audit2';
import { fetchSavedReportFromApi, normalizeReportPayload, openReportModal } from './reports.js?v=20260822-audit3';
import { clearCardPool, cleanupOrphanNoteModals } from './ui-core.js?v=20260819-audit2';
import { encodeStateToShortcode, decodeShortcodeToState, buildHybridShareUrl, build3DBridgeUrl } from './share-state.js?v=20260819-audit2';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';
import { resolveCatalogId } from './catalog-identity.js?v=20260819-audit2';
import { normalizePsuFormFactor } from './js/hardware-index.js?v=20260819-audit2';
import { syncCaseLayoutFilter } from './compatibility-engine.js?v=20260819-audit2';

export const DEFAULT_SITE_DESC = 'Compare cases, check PC hardware compatibility and fitment. Share build data and evaluation reports on 100’s of current & legacy mITX / mATX cases.';
export const DEFAULT_PAGE_TITLE = 'Case Database & Explorer - SFFLABS';
export const DEFAULT_UI_TITLE = 'Case Database & Explorer';

/* ==========================================================================
   META & SCHEMA DOM HELPERS
   ========================================================================== */

/**
 * Safely updates or creates a meta or link tag in <head>
 */
export function setHeadTag(selector, tagName, attrName, attrVal, contentVal) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute(attrName, attrVal);
    document.head.appendChild(el);
  }
  if (tagName === 'link') {
    el.setAttribute('href', contentVal);
  } else {
    el.setAttribute('content', contentVal);
  }
}

/**
 * Ensures Robots meta tag is reset to index, follow on standard case views
 */
export function resetRobotsMeta(directives = 'index, follow') {
  setHeadTag('meta[name="robots"]', 'meta', 'name', 'robots', directives);
}

/* ==========================================================================
   UI TITLE & ROUTING HELPERS
   ========================================================================== */

export function updateExplorerUiTitle(text) {
  const titleElements = document.querySelectorAll(
    '.sff-main-title, #explorerTitle, .explorer-title, .sff-main-heading, .compatibility-explorer-title'
  );
  if (titleElements.length > 0) {
    titleElements.forEach(el => {
      el.textContent = text || DEFAULT_UI_TITLE;
    });
  }
}

function collectBridgeRequirementsFromDom() {
  const value = id => document.getElementById(id)?.value;
  const combobox = selector => document.querySelector(`${selector} .combobox-input`)?.value?.trim() || undefined;
  return {
    mobo: value('moboType') || 'ITX',
    psu: value('psuType') || 'SFX',
    vol: value('maxVolume') || '10',
    gpuL: value('gpuLength') || '304',
    gpuT: value('gpuThickness') || '40',
    gpuH: value('gpuHeight') || '137',
    pcie: value('gpuPcieSlots') || '2',
    cool: value('coolingMode') || 'air',
    cpuH: value('cpuHeight') || '47',
    rad: value('radiatorSize') || '240',
    fan: value('radFanType') || 'standard',
    ramH: value('ramHeight') || '35',
    ramModules: value('ramModules') || '2',
    search: document.querySelector('#caseSearchCombobox .combobox-input')?.value?.trim() || undefined,
    gpuSearch: combobox('#gpuCombobox'),
    cpuSearch: combobox('#cpuCoolerCombobox'),
    aioSearch: combobox('#aioSearchCombobox') || combobox('#aioCoolerCombobox'),
    moboSearch: combobox('#moboCombobox'),
    ramSearch: combobox('#ramCombobox')
  };
}

export function bridgeCaseTo3DWorkbench(caseName) {
  if (typeof window.addCaseToMat === 'function') {
    window.addCaseToMat(caseName);
    document.getElementById('sff-canvas-viewport')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  const target = build3DBridgeUrl(caseName, collectBridgeRequirementsFromDom());
  window.open(target, '_blank');
}

export { encodeStateToShortcode, decodeShortcodeToState, buildHybridShareUrl, build3DBridgeUrl };

export function parseExplorerRoute(pathname = location.pathname) {
  const segments = String(pathname || '/').split('/').filter(Boolean);
  const dbIndex = segments.indexOf('db');
  if (dbIndex === -1) return { kind: 'other', segments };
  const tail = segments.slice(dbIndex + 1);
  if (!tail.length) return { kind: 'database', segments };

  if (tail[0] === 'compare') {
    const pair = tail[1] || '';
    const parts = pair.split('-vs-');
    return {
      kind: 'compare', segments, pair,
      slugA: parts.length === 2 ? parts[0] : '',
      slugB: parts.length === 2 ? parts[1] : '',
      stateToken: tail[2] || null
    };
  }
  if (tail[0] === 'cases') {
    return { kind: 'category', segments, category: tail[1] || null, stateToken: tail[2] || null };
  }
  if (tail[0] === 'build') {
    return { kind: 'build', segments, stateToken: tail[1] || null };
  }
  return { kind: 'case', segments, slug: tail[0], stateToken: tail[1] || null };
}

function commitExplorerPath(path, historyState = null, preferredIntent = null) {
  if (!path) {
    state.routeNavigationIntent = 'replace';
    return;
  }

  // This synchronizer owns pathname only. Preserve query and literal URL hash fragments
  // (#audit/#3d/#specs/etc.) rather than silently deleting them.
  const next = new URL(location.href);
  next.pathname = path;
  const target = `${next.pathname}${next.search}${next.hash}`;

  if (`${location.pathname}${location.search}${location.hash}` === target) {
    state.routeNavigationIntent = 'replace';
    return;
  }

  const intent = preferredIntent || state.routeNavigationIntent || 'replace';
  const method = intent === 'push' ? 'pushState' : 'replaceState';
  history[method](historyState, '', target);
  state.routeNavigationIntent = 'replace';
  clearCardPool();
  cleanupOrphanNoteModals();
}

export function isSingleCaseUrlView() {
  return parseExplorerRoute().kind === 'case';
}

export function renderSingleCaseBreadcrumbs(caseName) {
  let breadcrumbEl = document.getElementById('sffCaseBreadcrumb');
  
  if (!isSingleCaseUrlView() || !caseName) {
    if (breadcrumbEl) breadcrumbEl.remove();
    return;
  }

  if (!breadcrumbEl) {
    breadcrumbEl = document.createElement('nav');
    breadcrumbEl.id = 'sffCaseBreadcrumb';
    breadcrumbEl.className = 'sff-breadcrumb-nav font-mono';
  }

  const tabNav = document.getElementById('layoutFilterGroup') || document.querySelector('.sff-right-box-main .sff-docked-tab-bar');
  if (tabNav && tabNav.parentNode) {
    tabNav.parentNode.insertBefore(breadcrumbEl, tabNav.nextSibling);
  }

  const cleanCaseTitle = formatCaseBrandTitle(caseName);
  breadcrumbEl.innerHTML = `
    <a href="/db/" class="breadcrumb-link" data-sff-back-to-feed="true" aria-label="Back to database feed">DATABASE</a>
    <span class="breadcrumb-separator">/</span>
    <span class="breadcrumb-current">${escapeHtml(cleanCaseTitle)}</span>
  `;
}

export function processUrlHashActions(targetCaseIdx) {
  const hash = (location.hash || '').toLowerCase().trim();
  if (!hash || targetCaseIdx === null) return;

  setTimeout(() => {
    if (hash === '#audit' || hash === '#report') {
      const reportBtn = document.getElementById('generateReportBtn');
      if (reportBtn && !reportBtn.disabled) reportBtn.click();
    } else if (hash === '#3d' || hash === '#workbench') {
      const caseName = state.fetchedCasesGlobal ? state.fetchedCasesGlobal[targetCaseIdx]?.name : null;
      if (caseName) bridgeCaseTo3DWorkbench(caseName);
    } else if (hash === '#specs') {
      const card = document.getElementById(`card-${targetCaseIdx}`);
      const drawer = card?.querySelector('.extra-specs-drawer');
      if (drawer) drawer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 400);
}

export function togglePinCase(caseIdx, onEvaluateCallback) {
  state.routeNavigationIntent = 'push';
  const existingPos = state.sffPinnedCases.indexOf(caseIdx);

  if (existingPos !== -1) {
    state.sffPinnedCases.splice(existingPos, 1);
  } else {
    if (state.sffPinnedCases.length >= 2) {
      state.sffPinnedCases.shift();
    }
    state.sffPinnedCases.push(caseIdx);
  }
  window.sffPinnedCases = state.sffPinnedCases;

  syncComparisonUrlAndLayout(onEvaluateCallback);
}

export function syncComparisonUrlAndLayout(onEvaluateCallback) {
  const feed = document.getElementById('caseList');

  if (state.sffPinnedCases.length === 2) {
    const caseA = state.fetchedCasesGlobal ? state.fetchedCasesGlobal[state.sffPinnedCases[0]] : null;
    const caseB = state.fetchedCasesGlobal ? state.fetchedCasesGlobal[state.sffPinnedCases[1]] : null;

    if (caseA && caseB) {
      const slugA = caseRouteSlug(caseA);
      const slugB = caseRouteSlug(caseB);
      const titleA = formatCaseBrandTitle(caseA.name);
      const titleB = formatCaseBrandTitle(caseB.name);
      const comparePath = `/db/compare/${slugA}-vs-${slugB}/`;
      const compareUrl = `${window.location.origin}${comparePath}`;
      const compareTitle = `${titleA} vs ${titleB} - SFFLABS`;
      const compareDesc = `Side-by-side spec comparison between ${titleA} and ${titleB}. Compare volume, GPU clearance, CPU cooler height, and radiator fitment.`;

      if (location.pathname !== comparePath) {
        commitExplorerPath(comparePath, { compare: true });
      }

      document.title = compareTitle;
      updateExplorerUiTitle(`${titleA} VS ${titleB}`);

      // Sync Meta Tags for Comparison Route
      resetRobotsMeta('index, follow');
      setHeadTag('link[rel="canonical"]', 'link', 'rel', 'canonical', compareUrl);
      setHeadTag('meta[property="og:url"]', 'meta', 'property', 'og:url', compareUrl);
      setHeadTag('meta[name="description"]', 'meta', 'name', 'description', compareDesc);
      setHeadTag('meta[property="og:title"]', 'meta', 'property', 'og:title', compareTitle);
      setHeadTag('meta[property="og:description"]', 'meta', 'property', 'og:description', compareDesc);
      setHeadTag('meta[property="og:type"]', 'meta', 'property', 'og:type', 'website');

      if (feed) feed.classList.add('side-by-side-comparison');
    }
  } else {
    if (feed) feed.classList.remove('side-by-side-comparison');
    if (location.pathname.startsWith('/db/compare/')) {
      commitExplorerPath('/db/', null);
      document.title = DEFAULT_PAGE_TITLE;
      updateExplorerUiTitle(DEFAULT_UI_TITLE);

      const dbUrl = `${window.location.origin}/db/`;
      resetRobotsMeta('index, follow');
      setHeadTag('link[rel="canonical"]', 'link', 'rel', 'canonical', dbUrl);
      setHeadTag('meta[property="og:url"]', 'meta', 'property', 'og:url', dbUrl);
      setHeadTag('meta[name="description"]', 'meta', 'name', 'description', DEFAULT_SITE_DESC);
      setHeadTag('meta[property="og:title"]', 'meta', 'property', 'og:title', DEFAULT_PAGE_TITLE);
      setHeadTag('meta[property="og:description"]', 'meta', 'property', 'og:description', DEFAULT_SITE_DESC);
      setHeadTag('meta[property="og:type"]', 'meta', 'property', 'og:type', 'website');
    }
  }

  if (typeof onEvaluateCallback === 'function') {
    onEvaluateCallback(true);
  }
}

export function parseIncomingComparisonRoute() {
  const route = parseExplorerRoute();
  if (route.kind !== 'compare' || !route.slugA || !route.slugB || !state.fetchedCasesGlobal) return false;

  const idxA = getCaseIndexBySlug(route.slugA);
  const idxB = getCaseIndexBySlug(route.slugB);
  if (idxA === -1 || idxB === -1) return false;

  state.sffPinnedCases = [idxA, idxB];
  window.sffPinnedCases = state.sffPinnedCases;
  const caseA = state.fetchedCasesGlobal[idxA];
  const caseB = state.fetchedCasesGlobal[idxB];
  const titleA = formatCaseBrandTitle(caseA.name);
  const titleB = formatCaseBrandTitle(caseB.name);
  document.title = `${titleA} vs ${titleB} - SFFLABS`;
  updateExplorerUiTitle(`${titleA} VS ${titleB}`);
  document.getElementById('caseList')?.classList.add('side-by-side-comparison');
  return true;
}

/* ==========================================================================
   METADATA & SCHEMA HYDRATION ENGINE
   ========================================================================== */

export function hydrateCaseSeoMetadata(caseObj) {
  if (!caseObj) return;

  const formattedName = formatCaseBrandTitle(caseObj.name);
  const slug = caseRouteSlug(caseObj);
  const canonicalUrl = `${window.location.origin}/db/${slug}/`;
  const pageTitle = `${formattedName} Specs & Compatibility - SFFLABS`;
  const known = caseObj._known || {};
  const knownLimit = (flag, value) => known[flag] && Number.isFinite(Number(value)) ? Number(value) : null;
  const volume = Number.isFinite(Number(caseObj.volume ?? caseObj._volume)) && Number(caseObj.volume ?? caseObj._volume) > 0
    ? Number(caseObj.volume ?? caseObj._volume)
    : null;
  const maxGpu = knownLimit('maxGPU', caseObj._maxGPU);
  const maxGpuT = knownLimit('maxGPUT', caseObj._maxThick);
  const maxCpu = knownLimit('maxCPU', caseObj._maxCPU);
  const maxAio = knownLimit('maxAIO', caseObj._maxAIO);
  const descFacts = [];
  if (maxGpu !== null) descFacts.push(`Max GPU: ${maxGpu}mm`);
  if (maxCpu !== null) descFacts.push(`Max CPU Cooler: ${maxCpu}mm`);
  if (maxAio !== null) descFacts.push(`Max AIO: ${maxAio}mm`);
  const ogDesc = `Check hardware compatibility and clearance specs for ${formattedName}${volume !== null ? ` (${volume}L)` : ''}.${descFacts.length ? ` ${descFacts.join(', ')}.` : ''}`;

  // 1. Reset Robots to index, follow when viewing an explicit case
  resetRobotsMeta('index, follow');

  // 2. Synchronize Canonical & OG:URL
  setHeadTag('link[rel="canonical"]', 'link', 'rel', 'canonical', canonicalUrl);
  setHeadTag('meta[property="og:url"]', 'meta', 'property', 'og:url', canonicalUrl);

  // 3. Synchronize Standard Meta & Descriptions
  setHeadTag('meta[name="description"]', 'meta', 'name', 'description', ogDesc);
  setHeadTag('meta[property="og:title"]', 'meta', 'property', 'og:title', pageTitle);
  setHeadTag('meta[property="og:description"]', 'meta', 'property', 'og:description', ogDesc);
  setHeadTag('meta[property="og:type"]', 'meta', 'property', 'og:type', 'product');

  // 4. Image & Social Cards
  const defaultLogo = `${window.location.origin}/wp-content/uploads/assets/images/logo-og.png`;
  const caseImgUrl = caseObj.image ? (caseObj.image.startsWith('http') ? caseObj.image : window.location.origin + caseObj.image) : defaultLogo;

  setHeadTag('meta[property="og:image"]', 'meta', 'property', 'og:image', caseImgUrl);
  setHeadTag('meta[name="twitter:card"]', 'meta', 'name', 'twitter:card', caseObj.image ? 'summary_large_image' : 'summary');
  setHeadTag('meta[name="twitter:title"]', 'meta', 'name', 'twitter:title', pageTitle);
  setHeadTag('meta[name="twitter:description"]', 'meta', 'name', 'twitter:description', ogDesc);
  setHeadTag('meta[name="twitter:image"]', 'meta', 'name', 'twitter:image', caseImgUrl);

  // 5. Cleanup leftover Build Schema if transitioning away from a shortcode route
  document.getElementById('sff-build-jsonld-schema')?.remove();

  // 6. Synchronize Product Schema (#sff-jsonld-schema)
  let schemaScript = document.getElementById('sff-jsonld-schema');
  if (!schemaScript) {
    schemaScript = document.createElement('script');
    schemaScript.id = 'sff-jsonld-schema';
    schemaScript.type = 'application/ld+json';
    document.head.appendChild(schemaScript);
  }

  const brandName = String(caseObj.brand || caseObj.manufacturer || caseObj.mfr || '').trim();
  const rawPrice = caseObj.priceUSD ?? caseObj.price ?? caseObj._priceVal;
  const parsedPrice = typeof rawPrice === 'number'
    ? rawPrice
    : parseFloat(String(rawPrice || '').replace(/[^0-9.]/g, ''));
  const hasRealPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;

  const rawAvailability = String(caseObj.availability || caseObj.stock_status || caseObj.stockStatus || '').toLowerCase();
  let schemaAvailability = null;
  if (caseObj._isLegacy || caseObj.isLegacy) {
    schemaAvailability = 'https://schema.org/Discontinued';
  } else if (['instock', 'in_stock', 'in stock', 'available'].includes(rawAvailability)) {
    schemaAvailability = 'https://schema.org/InStock';
  } else if (['outofstock', 'out_of_stock', 'out of stock', 'unavailable'].includes(rawAvailability)) {
    schemaAvailability = 'https://schema.org/OutOfStock';
  } else if (['preorder', 'pre_order', 'pre-order'].includes(rawAvailability)) {
    schemaAvailability = 'https://schema.org/PreOrder';
  }

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": formattedName,
    "image": [caseImgUrl],
    "description": ogDesc,
    "additionalProperty": []
  };

  if (volume !== null) schemaData.additionalProperty.push({ "@type": "PropertyValue", "name": "Volume", "value": `${volume} L` });
  if (maxGpu !== null) schemaData.additionalProperty.push({ "@type": "PropertyValue", "name": "Max GPU Length", "value": `${maxGpu} mm` });
  if (maxGpuT !== null) schemaData.additionalProperty.push({ "@type": "PropertyValue", "name": "Max GPU Thickness", "value": `${maxGpuT} mm` });
  if (maxCpu !== null) schemaData.additionalProperty.push({ "@type": "PropertyValue", "name": "Max CPU Height", "value": `${maxCpu} mm` });
  if (maxAio !== null) schemaData.additionalProperty.push({ "@type": "PropertyValue", "name": "Max AIO Radiator", "value": `${maxAio} mm` });

  if (brandName) {
    schemaData.brand = { "@type": "Brand", "name": brandName };
  }

  if (hasRealPrice) {
    schemaData.offers = {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": String(caseObj.priceCurrency || caseObj.currency || 'USD').toUpperCase(),
      "price": String(parsedPrice),
      "itemCondition": "https://schema.org/NewCondition"
    };
    if (schemaAvailability) schemaData.offers.availability = schemaAvailability;
  }

  schemaScript.textContent = JSON.stringify(schemaData);

  // 7. Synchronize FAQ Schema (#sff-faq-jsonld-schema)
  let faqScript = document.getElementById('sff-faq-jsonld-schema');
  if (!faqScript) {
    faqScript = document.createElement('script');
    faqScript.id = 'sff-faq-jsonld-schema';
    faqScript.type = 'application/ld+json';
    document.head.appendChild(faqScript);
  }

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is the maximum GPU clearance for ${formattedName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": maxGpu === null
            ? `Published maximum GPU clearance data for ${formattedName} is currently unavailable; verify case-specific documentation.`
            : (maxGpuT === null
              ? `The ${formattedName} has a published maximum GPU length of ${maxGpu}mm; GPU thickness data is currently unavailable.`
              : `The ${formattedName} supports graphics cards up to ${maxGpu}mm in length and ${maxGpuT}mm in thickness.`)
        }
      },
      {
        "@type": "Question",
        "name": `What is the maximum CPU cooler height for ${formattedName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": maxCpu === null
            ? `Published maximum CPU cooler height data for ${formattedName} is currently unavailable; verify case-specific documentation.`
            : `The ${formattedName} supports CPU coolers up to ${maxCpu}mm in height.`
        }
      },
      {
        "@type": "Question",
        "name": `Does ${formattedName} support liquid cooling radiators?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": maxAio === null
            ? `Published AIO radiator support data for ${formattedName} is currently unavailable; verify case-specific documentation.`
            : (maxAio > 0
              ? `Yes, ${formattedName} supports AIO liquid cooling radiators up to ${maxAio}mm.`
              : `No published AIO radiator support is listed for ${formattedName}.`)
        }
      }
    ]
  };
  faqScript.textContent = JSON.stringify(faqData);

  // 8. Synchronize Breadcrumb Schema (#sff-breadcrumb-jsonld-schema)
  let breadcrumbScript = document.getElementById('sff-breadcrumb-jsonld-schema');
  if (!breadcrumbScript) {
    breadcrumbScript = document.createElement('script');
    breadcrumbScript.id = 'sff-breadcrumb-jsonld-schema';
    breadcrumbScript.type = 'application/ld+json';
    document.head.appendChild(breadcrumbScript);
  }

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Database", "item": `${window.location.origin}/db/` },
      { "@type": "ListItem", "position": 2, "name": formattedName, "item": canonicalUrl }
    ]
  };
  breadcrumbScript.textContent = JSON.stringify(breadcrumbData);
}

export function syncCaseUrlAndTitle(searchQuery, applyCaseCompatibleDefaults = null) {
  if (!state.fetchedCasesGlobal || !state.fetchedCasesGlobal.length) return;
  const currentRoute = parseExplorerRoute();
  if (currentRoute.kind === 'compare' || state.sffPinnedCases.length === 2) return;

  const query = (searchQuery || '').trim();

  // Tokenized build URLs are immutable snapshots while they are merely being restored or
  // re-evaluated. Once the user deliberately chooses/types another case (or loads another
  // documented build), leave snapshot-route ownership exactly once and resume ordinary
  // canonical routing. Previously this unconditional early-return trapped /db on the old
  // /<case>/<token>/ path and prevented a new case selection from taking ownership.
  if (currentRoute.stateToken && !state.portableRouteExitRequested) {
    state.initialShortcodeUrl = location.pathname;
    window._initialShortcodeUrl = location.pathname;
    return;
  }
  if (currentRoute.stateToken && state.portableRouteExitRequested) {
    state.initialShortcodeUrl = null;
    window._initialShortcodeUrl = null;
    state.portableFixedStep = null;
    state.portableBuildHardware = { psu: null, ram: null };
    state.portableRouteExitRequested = false;
  } else if (!currentRoute.stateToken && state.portableRouteExitRequested) {
    // The intent flag is edge-triggered; never let a normal clean route carry it forward into
    // some later history/restoration transaction.
    state.portableRouteExitRequested = false;
  }
  if (state.initialShortcodeUrl && state.initialShortcodeUrl === location.pathname && !state.portableRouteExitRequested) return;


  if (!query) {
    state.lastHydratedCaseSlug = null;
    window._lastHydratedCaseSlug = null;

    if (location.pathname !== '/db/' && location.pathname !== '/db') {
      commitExplorerPath('/db/', null);
    }

    document.title = DEFAULT_PAGE_TITLE;
    updateExplorerUiTitle(DEFAULT_UI_TITLE);
    renderSingleCaseBreadcrumbs('');

    // Reset Meta Tags & Schemas back to Base Database State
    const dbUrl = `${window.location.origin}/db/`;
    resetRobotsMeta('index, follow');
    setHeadTag('link[rel="canonical"]', 'link', 'rel', 'canonical', dbUrl);
    setHeadTag('meta[property="og:url"]', 'meta', 'property', 'og:url', dbUrl);
    setHeadTag('meta[name="description"]', 'meta', 'name', 'description', DEFAULT_SITE_DESC);
    setHeadTag('meta[property="og:title"]', 'meta', 'property', 'og:title', DEFAULT_PAGE_TITLE);
    setHeadTag('meta[property="og:description"]', 'meta', 'property', 'og:description', DEFAULT_SITE_DESC);
    setHeadTag('meta[property="og:type"]', 'meta', 'property', 'og:type', 'website');

    document.getElementById('sff-jsonld-schema')?.remove();
    document.getElementById('sff-faq-jsonld-schema')?.remove();
    document.getElementById('sff-build-jsonld-schema')?.remove();
    document.getElementById('sff-breadcrumb-jsonld-schema')?.remove();
    return;
  }

  const exactMatch = getCaseByName(query) || getCaseBySlug(query);

  if (exactMatch) {
    const slug = caseRouteSlug(exactMatch);

    if (state.lastHydratedCaseSlug !== slug && !state.isRestoringShortcodeState) {
      state.lastHydratedCaseSlug = slug;
      window._lastHydratedCaseSlug = slug;
      if (typeof applyCaseCompatibleDefaults === 'function') applyCaseCompatibleDefaults(exactMatch);

      const matchedIdx = getCaseIndexBySlug(slug);
      if (matchedIdx !== -1) {
        state.selectedCaseIdx = matchedIdx;
      }
    }

    const targetPath = `/db/${slug}/`;

    if (location.pathname !== targetPath) {
      commitExplorerPath(targetPath, { caseSlug: slug });
    }
    const cleanTitle = formatCaseBrandTitle(exactMatch.name);
    document.title = `${cleanTitle} Specs & Compatibility - SFFLABS`;
    updateExplorerUiTitle(cleanTitle);

    hydrateCaseSeoMetadata(exactMatch);
    renderSingleCaseBreadcrumbs(exactMatch.name);
  } else {
    state.lastHydratedCaseSlug = null;
    window._lastHydratedCaseSlug = null;

    if (location.pathname !== '/db/' && location.pathname !== '/db') {
      commitExplorerPath('/db/', null);
    }

    document.title = DEFAULT_PAGE_TITLE;
    updateExplorerUiTitle(DEFAULT_UI_TITLE);
    renderSingleCaseBreadcrumbs('');

    const dbUrl = `${window.location.origin}/db/`;
    resetRobotsMeta('index, follow');
    setHeadTag('link[rel="canonical"]', 'link', 'rel', 'canonical', dbUrl);
    setHeadTag('meta[property="og:url"]', 'meta', 'property', 'og:url', dbUrl);
    setHeadTag('meta[name="description"]', 'meta', 'name', 'description', DEFAULT_SITE_DESC);
    setHeadTag('meta[property="og:title"]', 'meta', 'property', 'og:title', DEFAULT_PAGE_TITLE);
    setHeadTag('meta[property="og:description"]', 'meta', 'property', 'og:description', DEFAULT_SITE_DESC);
    setHeadTag('meta[property="og:type"]', 'meta', 'property', 'og:type', 'website');
  }
}

export function initPopStateRouter(dom, evaluateCompatibility, applyCaseCompatibleDefaults = null) {
  window.addEventListener('popstate', async () => {
    if (!state.fetchedCasesGlobal?.length) return;
    clearCardPool();
    cleanupOrphanNoteModals();
    state.routeNavigationIntent = 'replace';

    const route = parseExplorerRoute();
    try {
      if (route.kind === 'compare') {
        state.selectedCaseIdx = null;
        parseIncomingComparisonRoute();
        if (route.stateToken) {
          await parseIncomingUrlParams(dom, evaluateCompatibility, applyCaseCompatibleDefaults, null, null);
        } else {
          evaluateCompatibility(true);
        }
        return;
      }

      if (route.kind === 'case') {
        const matched = getCaseBySlug(route.slug);
        if (matched) {
          if (dom.search) dom.search.value = matched.name;
          const matchedIdx = getCaseIndexBySlug(route.slug);
          state.selectedCaseIdx = matchedIdx >= 0 ? matchedIdx : null;
          if (state.lastHydratedCaseSlug !== route.slug) {
            state.lastHydratedCaseSlug = route.slug;
            window._lastHydratedCaseSlug = route.slug;
            if (typeof applyCaseCompatibleDefaults === 'function') applyCaseCompatibleDefaults(matched);
          }
          if (route.stateToken) {
            await parseIncomingUrlParams(dom, evaluateCompatibility, applyCaseCompatibleDefaults, null, null);
          } else {
            evaluateCompatibility(true);
          }
          return;
        }
      }

      if (route.kind === 'build' && route.stateToken) {
        state.selectedCaseIdx = null;
        await parseIncomingUrlParams(dom, evaluateCompatibility, applyCaseCompatibleDefaults, null, null);
        return;
      }

      if (dom.search) dom.search.value = '';
      state.selectedCaseIdx = null;
      state.initialShortcodeUrl = null;
      state.portableRouteExitRequested = false;
      state.lastHydratedCaseSlug = null;
      window._initialShortcodeUrl = null;
      window._lastHydratedCaseSlug = null;
      evaluateCompatibility(true);
    } catch (error) {
      debugRuntimeIssue('Popstate Router', error);
      evaluateCompatibility(true);
    }
  });
}

export async function parseIncomingUrlParams(dom, evaluateCompatibility, applyCaseCompatibleDefaults, updateSliderBadges, cardPool) {
  let urlState = null;
  let isHybridRoute = false;
  let isCleanCaseRoute = false;
  let loadedCaseName = null;
  const route = parseExplorerRoute();

  if (route.kind === 'compare' && route.stateToken) {
    urlState = decodeShortcodeToState(route.stateToken);
    if (urlState) {
      isHybridRoute = true;
      loadedCaseName = route.pair;
      state.initialShortcodeUrl = location.pathname;
      window._initialShortcodeUrl = location.pathname;
    }
  }

  if (!urlState && (route.kind === 'case' || route.kind === 'build')) {
    const candidateSlug = route.kind === 'build' ? 'build' : route.slug;
    const candidateShortcode = route.stateToken;

    if (candidateShortcode) {
      urlState = decodeShortcodeToState(candidateShortcode);
      if (urlState) {
        isHybridRoute = true;
        loadedCaseName = candidateSlug;
        state.initialShortcodeUrl = location.pathname;
        window._initialShortcodeUrl = location.pathname;
      }
    }

    if (!urlState && candidateSlug) {
      urlState = decodeShortcodeToState(candidateSlug);
      if (urlState) {
        isHybridRoute = true;
        loadedCaseName = 'build';
        state.initialShortcodeUrl = location.pathname;
        window._initialShortcodeUrl = location.pathname;
      }
    }

    if (!urlState && candidateSlug && route.kind === 'case') {
      const slugClean = String(candidateSlug).toLowerCase().trim();
      const matchedCase = getCaseBySlug(slugClean);
        
      const rawCaseName = matchedCase ? matchedCase.name : candidateSlug;
      const formattedTitle = formatCaseBrandTitle(rawCaseName);

      if (dom && dom.search) dom.search.value = rawCaseName;

      const comboboxInput = document.querySelector('#caseSearchCombobox .combobox-input');
      if (comboboxInput) comboboxInput.value = rawCaseName;

      if (matchedCase && state.lastHydratedCaseSlug !== slugClean) {
        state.lastHydratedCaseSlug = slugClean;
        window._lastHydratedCaseSlug = slugClean;
        if (typeof applyCaseCompatibleDefaults === 'function') applyCaseCompatibleDefaults(matchedCase);
      }

      document.title = `${formattedTitle} Specs & Compatibility - SFFLABS`;
      updateExplorerUiTitle(formattedTitle);
      if (matchedCase) hydrateCaseSeoMetadata(matchedCase);
      renderSingleCaseBreadcrumbs(rawCaseName);
      isCleanCaseRoute = true;
    }
  }

  // Portable v2 build tokens are identity-first. The backend catalog ID wins over
  // the human-readable route slug when both are present.
  const portableBuild = urlState?._portableBuild || null;
  if (portableBuild?.case?.id) {
    const caseHit = resolveCatalogId(portableBuild.case.id, 'case');
    if (caseHit?.record?.name) loadedCaseName = caseHit.record.name;
  }

  const ps = new URLSearchParams(location.search);
  if (!urlState && !isCleanCaseRoute && (ps.has('sff_import') || ps.has('report_id'))) {
    urlState = {
      mobo: ps.get('mobo'), psu: ps.get('psu'), vol: ps.get('vol'),
      gpuL: ps.get('gpuL'), gpuT: ps.get('gpuT'), gpuH: ps.get('gpuH'),
      pcie: ps.get('pcie'), cool: ps.get('cool'), cpuH: ps.get('cpuH'),
      rad: ps.get('rad'), fan: ps.get('fan'), ramH: ps.get('ramH'), ramModules: ps.get('ramModules'), search: ps.get('search'),
      gpuSearch: ps.get('gpuSearch'), cpuSearch: ps.get('cpuSearch'),
      aioSearch: ps.get('aioSearch'), moboSearch: ps.get('moboSearch'), ramSearch: ps.get('ramSearch'), report_id: ps.get('report_id')
    };
  }

  if (isCleanCaseRoute && !urlState) {
    clearCardPool();
    evaluateCompatibility();
    return true;
  }

  if (!urlState) return false;

  state.isRestoringShortcodeState = true;
  window._isRestoringShortcodeState = true;
  // URL state describes effective/manual requirements. Exact identities are restored below
  // only when the v2 token contains a backend catalog ID.
  clearAllSelectedComponents();
  if (portableBuild) {
    state.portableBuildHardware = {
      psu: portableBuild.hardware?.psu || null,
      ram: portableBuild.hardware?.ram || null
    };
    state.portableFixedStep = portableBuild.fixedStep || null;
  } else {
    state.portableBuildHardware = { psu: null, ram: null };
    state.portableFixedStep = null;
  }

  const syncSegment = (gid, val) => {
    if (!val) return;
    const btn = [...document.querySelectorAll(`#${gid} .segment-btn`)].find(x => {
      const segmentValue = x.getAttribute('data-value');
      return segmentValue && segmentValue.toLowerCase() === String(val).toLowerCase();
    });
    if (btn) btn.click();
  };

  if (urlState.mobo) syncSegment('moboSegmented', urlState.mobo);
  if (urlState.psu) syncSegment('psuSegmented', urlState.psu);
  if (urlState.cool) syncSegment('coolingModeSegmented', urlState.cool);
  if (urlState.rad) syncSegment('radiatorSizeSegmented', urlState.rad);
  if (urlState.fan) syncSegment('radFanSegmented', urlState.fan);
  if (urlState.pcie) syncSegment('gpuPcieSegmented', urlState.pcie);
  if (urlState.ramModules) syncSegment('ramModuleSegmented', urlState.ramModules);

  const setInputValue = (id, val) => {
    const el = document.getElementById(id);
    if (val !== undefined && val !== null && el) el.value = val;
  };

  setInputValue('maxVolume', urlState.vol);
  setInputValue('gpuLength', urlState.gpuL);
  setInputValue('gpuThickness', urlState.gpuT);
  setInputValue('gpuHeight', urlState.gpuH);
  setInputValue('gpuPcieSlots', urlState.pcie);
  setInputValue('cpuHeight', urlState.cpuH);
  setInputValue('ramHeight', urlState.ramH);
  setInputValue('ramModules', urlState.ramModules);

  if (urlState.search || (loadedCaseName && loadedCaseName !== 'build')) {
    const searchVal = urlState.search || loadedCaseName;
    if (dom && dom.search) dom.search.value = searchVal;
    const comboboxInput = document.querySelector('#caseSearchCombobox .combobox-input');
    if (comboboxInput) comboboxInput.value = searchVal;

    const matchedCase = getCaseByName(searchVal) || getCaseBySlug(searchVal);
    if (matchedCase) {
      state.lastHydratedCaseSlug = caseRouteSlug(matchedCase);
      window._lastHydratedCaseSlug = state.lastHydratedCaseSlug;
    }
  } else if (loadedCaseName === 'build') {
    if (dom && dom.search) dom.search.value = '';
    const comboboxInput = document.querySelector('#caseSearchCombobox .combobox-input');
    if (comboboxInput) comboboxInput.value = '';
  }

  const gi = document.querySelector('#gpuCombobox .combobox-input'),
        ci = document.querySelector('#cpuCoolerCombobox .combobox-input'),
        mi = document.querySelector('#moboCombobox .combobox-input'),
        ri = document.querySelector('#ramCombobox .combobox-input');

  if (urlState.gpuSearch && gi) gi.value = urlState.gpuSearch;
  if (urlState.cpuSearch && ci) ci.value = urlState.cpuSearch;
  if (urlState.aioSearch) syncComboboxInputValues(AIO_COMBOBOX_IDS, urlState.aioSearch);
  if (urlState.moboSearch && mi) mi.value = urlState.moboSearch;
  if (urlState.ramSearch && ri) ri.value = urlState.ramSearch;

  if (portableBuild) {
    const activeCase = portableBuild.case?.id
      ? resolveCatalogId(portableBuild.case.id, 'case')?.record
      : (loadedCaseName ? (getCaseByName(loadedCaseName) || getCaseBySlug(loadedCaseName)) : null);

    if (activeCase) {
      const activeIndex = state.fetchedCasesGlobal.indexOf(activeCase);
      if (activeIndex >= 0) state.selectedCaseIdx = activeIndex;
      const activeName = String(activeCase.name || '').trim();
      if (activeName) {
        if (dom?.search) dom.search.value = activeName;
        const caseInput = document.querySelector('#caseSearchCombobox .combobox-input');
        if (caseInput) caseInput.value = activeName;
        state.lastHydratedCaseSlug = caseRouteSlug(activeCase);
        window._lastHydratedCaseSlug = state.lastHydratedCaseSlug;
      }

      // Tokenized builds intentionally bypass applyCaseCompatibleDefaults() so imported
      // hardware is not wiped. Still synchronize the physical-layout tab to the case.
      syncCaseLayoutFilter(activeCase);

      if (portableBuild.modeId) {
        const resolvedMode = portableBuild.modeId === 'default'
          ? 'default'
          : resolveCaseModeId(activeCase, portableBuild.modeId, null);
        if (resolvedMode) {
          setCaseModeSelection(activeCase, resolvedMode);
          state.selectedModeId = resolvedMode;
        }
      }
    }

    const clickCatalogItem = (containerId, ref) => {
      if (!ref?.id) return false;
      const container = document.getElementById(containerId);
      let item = container?._sffFindBestItem?.({ id: ref.id, catalogId: ref.id, name: '' }) || null;
      if (!item) {
        container?._sffEnsureOptions?.();
        item = [...document.querySelectorAll(`#${containerId} .combobox-item[data-catalog-id]`)]
          .find(node => String(node.getAttribute('data-catalog-id') || '').toLowerCase() === String(ref.id).toLowerCase()) || null;
      }
      if (!item) return false;
      item.click();
      return true;
    };

    const inferMotherboardFormFactor = (ref) => {
      if (!ref?.custom || !ref.dims) return '';
      const dims = [Number(ref.dims.l) || 0, Number(ref.dims.w) || 0, Number(ref.dims.h) || 0]
        .filter(value => value > 0).sort((a, b) => b - a);
      const longest = dims[0] || 0;
      if (longest >= 290) return 'ATX';
      if (longest >= 225) return 'mATX';
      return longest > 0 ? 'ITX' : '';
    };

    const inferPsuFormFactor = (ref) => {
      if (!ref?.custom || !ref.dims) return '';
      const dims = [Number(ref.dims.l) || 0, Number(ref.dims.w) || 0, Number(ref.dims.h) || 0]
        .filter(value => value > 0).sort((a, b) => a - b);
      if (!dims.length) return '';
      if (dims[0] <= 45) return 'FLEX';
      if (dims.some(value => value >= 82 && value <= 92) && dims.some(value => value >= 140)) return 'ATX';
      const longAxis = Math.max(...dims);
      return longAxis >= 120 && dims.filter(value => value >= 118).length >= 2 ? 'SFX-L' : 'SFX';
    };

    const inferRadiatorNominalSize = (ref) => {
      if (!ref?.custom || !ref.dims) return 0;
      const labelMatch = String(ref.label || '').match(/(?:^|\D)(120|240|280|360)(?:\D|$)/);
      if (labelMatch) return Number(labelMatch[1]);
      const longest = Math.max(Number(ref.dims.l) || 0, Number(ref.dims.w) || 0);
      if (longest >= 350) return 360;
      if (longest >= 295) return 280;
      if (longest >= 220) return 240;
      return longest > 0 ? 120 : 0;
    };

    const importedBoard = portableBuild.hardware?.motherboard;
    const boardLoaded = clickCatalogItem('moboCombobox', importedBoard);
    if (!boardLoaded && importedBoard?.custom && !urlState.mobo) {
      const formFactor = inferMotherboardFormFactor(importedBoard);
      if (formFactor) syncSegment('moboSegmented', formFactor);
    }

    const importedGpu = portableBuild.hardware?.gpu;
    const gpuLoaded = clickCatalogItem('gpuCombobox', importedGpu);
    if (!gpuLoaded && importedGpu?.custom) {
      setInputValue('gpuLength', importedGpu.dims?.l);
      setInputValue('gpuThickness', importedGpu.dims?.w);
      setInputValue('gpuHeight', importedGpu.dims?.h);
    }

    const importedAir = portableBuild.hardware?.aircooler;
    const airLoaded = clickCatalogItem('cpuCoolerCombobox', importedAir);
    if (!airLoaded && importedAir?.custom) {
      syncSegment('coolingModeSegmented', 'air');
      setInputValue('cpuHeight', importedAir.dims?.h);
    }

    const importedAio = portableBuild.hardware?.aio;
    const aioLoaded = clickCatalogItem('aioCoolerCombobox', importedAio) || clickCatalogItem('aioSearchCombobox', importedAio);
    if (!aioLoaded && importedAio?.custom) {
      syncSegment('coolingModeSegmented', 'liquid');
      if (!urlState.rad) {
        const nominalSize = inferRadiatorNominalSize(importedAio);
        if (nominalSize) syncSegment('radiatorSizeSegmented', nominalSize);
      }
    }

    const importedRam = portableBuild.hardware?.ram;
    const ramLoaded = clickCatalogItem('ramCombobox', importedRam);
    if (importedRam) {
      const modules = Math.max(1, Math.min(8, Number(importedRam.modules) || Number(urlState.ramModules) || 2));
      syncSegment('ramModuleSegmented', modules);
      setInputValue('ramModules', modules);
      if (!ramLoaded && importedRam.custom === true) setInputValue('ramHeight', importedRam.dims?.h || urlState.ramH || 35);
      if (!ramLoaded) state.portableBuildHardware.ram = importedRam;
    }

    const importedPsu = portableBuild.hardware?.psu;
    const psuLoaded = clickCatalogItem('psuCombobox', importedPsu);
    if (!psuLoaded && !urlState.psu && importedPsu?.custom) {
      const inferredPsu = inferPsuFormFactor(importedPsu);
      if (inferredPsu) syncSegment('psuSegmented', inferredPsu);
    }
    if (!psuLoaded && importedPsu?.id) {
      // Compatibility fallback for pages where the PSU combobox has not yet been
      // deployed. The new UI normally restores exact PSU identity through it.
      const psuHit = resolveCatalogId(importedPsu.id, 'psu');
      const record = psuHit?.record || null;
      if (record) {
        const rawForm = record.formFactor ?? record.form_factor ?? record.psuType ?? record.type ?? urlState.psu;
        const normalizedForm = normalizePsuFormFactor(rawForm);
        const formFactor = normalizedForm === 'DC' ? 'DC-ATX' : (normalizedForm || String(urlState.psu || 'SFX').toUpperCase());
        syncSegment('psuSegmented', formFactor);
        setSelectedComponent('psu', {
          name: record.name || record.model || record.title || 'Power Supply',
          form_factor: formFactor,
          length_mm: parseSpecNum(record.length ?? record.length_mm ?? record.psuLength, 0),
          width_mm: parseSpecNum(record.width ?? record.width_mm, 0),
          height_mm: parseSpecNum(record.height ?? record.height_mm, 0),
          wattage: parseSpecNum(record.wattage ?? record.watts ?? record.power, 0),
          catalogId: importedPsu.id
        });
        state.portableBuildHardware.psu = null;
      }
    } else if (!psuLoaded && importedPsu) {
      state.portableBuildHardware.psu = importedPsu;
    }
  }

  if (updateSliderBadges) updateSliderBadges();
  clearCardPool();
  evaluateCompatibility();

  state.isRestoringShortcodeState = false;
  window._isRestoringShortcodeState = false;

  if (isHybridRoute) {
    if (loadedCaseName && loadedCaseName !== 'build') {
      const cleanCase = formatCaseBrandTitle(loadedCaseName);
      document.title = generateBuildShareTitle(loadedCaseName, urlState);
      updateExplorerUiTitle(`${cleanCase} Build`);
      renderSingleCaseBreadcrumbs(loadedCaseName);
    } else {
      document.title = "Shared Build - SFFLABS";
      updateExplorerUiTitle(DEFAULT_UI_TITLE);
      renderSingleCaseBreadcrumbs('');
    }
  }

  if (urlState.report_id) {
    const repHash = String(urlState.report_id).replace('#', '');
    const rawReport = await fetchSavedReportFromApi(repHash);
    if (rawReport) {
      state.activeAuditRequestPayload = null;
      const normalized = normalizeReportPayload(rawReport);
      const caseName = normalized?.case_name || normalized?.target_case?.normalized_specs?.name || rawReport?.case_name || "SFF BUILD";
      setTimeout(() => {
        openReportModal(normalized, caseName, repHash);
      }, 300);
    }
  }
  return true;
}
